import { config } from './config';
import { getAccessToken } from './auth';

async function fetchWithAuth(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init?.headers ?? {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
}

export async function listTemplates(): Promise<string[]> {
  const res = await fetchWithAuth(`${config.apiBase}/api/templates`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function uploadTemplates(files: File[]): Promise<string[]> {
  const form = new FormData();
  files.forEach(file => form.append('files', file));
  const res = await fetchWithAuth(`${config.apiBase}/api/templates`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function deleteTemplate(filename: string): Promise<void> {
  const res = await fetchWithAuth(`${config.apiBase}/api/templates/${encodeURIComponent(filename)}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function getTemplateBytes(filename: string): Promise<ArrayBuffer> {
  const res = await fetchWithAuth(`${config.apiBase}/api/templates/${encodeURIComponent(filename)}/bytes`);
  if (!res.ok) throw new Error(await res.text());
  return res.arrayBuffer();
}

export async function downloadTemplate(filename: string): Promise<void> {
  const buffer = await getTemplateBytes(filename);
  const url = URL.createObjectURL(new Blob([buffer]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Current user ──────────────────────────────────────────────────────────────

export type MeResponse = { email: string; roles: string[] };

export async function getMe(): Promise<MeResponse> {
  const res = await fetchWithAuth(`${config.apiBase}/api/me`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export type AdminUsersResponse = { connected: string[]; known: string[] };

export async function adminListUsers(): Promise<AdminUsersResponse> {
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminListTemplates(email: string): Promise<string[]> {
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/templates`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminUploadTemplates(email: string, files: File[]): Promise<string[]> {
  const form = new FormData();
  files.forEach(file => form.append('files', file));
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/templates`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminDeleteTemplate(email: string, filename: string): Promise<void> {
  const res = await fetchWithAuth(
    `${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/templates/${encodeURIComponent(filename)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw new Error(await res.text());
}

export async function adminGetTemplateBytes(email: string, filename: string): Promise<ArrayBuffer> {
  const res = await fetchWithAuth(
    `${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/templates/${encodeURIComponent(filename)}/bytes`,
  );
  if (!res.ok) throw new Error(await res.text());
  return res.arrayBuffer();
}

export async function adminLockUser(email: string): Promise<void> {
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/lock`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text());
}

export async function adminUnlockUser(email: string): Promise<void> {
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/lock`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
}

export async function adminWatchUser(email: string): Promise<void> {
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/watch`, { method: 'POST' });
  if (!res.ok) throw new Error(await res.text());
}

export async function adminUnwatchUser(email: string): Promise<void> {
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/watch`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await res.text());
}

export async function adminGetTemplateRules(email: string): Promise<Record<string, TemplateRule>> {
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/template-rules`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function adminSaveTemplateRules(email: string, rules: Record<string, TemplateRule>): Promise<void> {
  const res = await fetchWithAuth(`${config.apiBase}/api/admin/users/${encodeURIComponent(email)}/template-rules`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rules),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function adminDownloadTemplate(email: string, filename: string): Promise<void> {
  const buffer = await adminGetTemplateBytes(email, filename);
  const url = URL.createObjectURL(new Blob([buffer]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Data index ────────────────────────────────────────────────────────────────

export type DataIndexResponse = {
  dataIndexId: string;
  lookupKeys: string[];
  entries: { lookupKey: string; lookupKeyParts: Record<string, string> }[];
  columnHeaders: string[];
};

export type TemplateRule = {
  folder: string;
  filenameTemplate: string;
};

export async function getTemplateRules(): Promise<Record<string, TemplateRule>> {
  const res = await fetchWithAuth(`${config.apiBase}/api/template-rules`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function saveTemplateRules(rules: Record<string, TemplateRule>): Promise<void> {
  const res = await fetchWithAuth(`${config.apiBase}/api/template-rules`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rules),
  });
  if (!res.ok) throw new Error(await res.text());
}

export async function uploadDataIndex(file: File, lookupKey: string): Promise<DataIndexResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('lookupKey', lookupKey);
  const res = await fetchWithAuth(`${config.apiBase}/api/data-index`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export type GenerateRequest = {
  dataIndexId: string;
  lookupKey: string;
  commands: {
    lookupKeyParts: Record<string, string>;
    templates: string[];
  }[];
};

export async function generateZip(payload: GenerateRequest): Promise<Blob> {
  const res = await fetchWithAuth(`${config.apiBase}/api/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.blob();
}
